// @file Detects Microsoft OneDrive / SharePoint sharing URLs pasted or typed as post attachments.
// Mirrors Integrations::MicrosoftOneDriveSharingLink.sharing_url? on the Rails side.

const ONEDRIVE_HOSTS = new Set(['1drv.ms', 'onedrive.live.com'])
const SHAREPOINT_HOST_PATTERN = /(^|\.)sharepoint\.com$/i
// SharePoint sharing links carry a single-letter type segment, e.g. /:w:/g/... or /:b:/s/...
const SHAREPOINT_SHARING_PATH_PATTERN = /\/:[a-z]:\//i
// The address-bar URL of a document open in Office Online / OneNote Online, which is what people
// copy from the browser: /personal/<user>/_layouts/15/Doc.aspx?sourcedoc={guid}&... Graph's shares
// endpoint resolves it like a sharing link. Other _layouts pages (the OneDrive browse page, site
// pages) are not documents and are left to the generic link preview.
const SHAREPOINT_DOCUMENT_PATH_PATTERN = /\/_layouts\/15\/doc\.aspx$/i
// The OneDrive for Business browse page with an item selected, which is the address-bar URL for a
// file that has no Office Online editor (images, PDFs, videos): /my?id=/personal/<user>/Documents/x.png
// or /personal/<user>/_layouts/15/onedrive.aspx?id=... Graph resolves it to the item at `id`. A folder
// id resolves too, which the server rejects since a folder has nothing to preview.
const SHAREPOINT_BROWSE_PATH_PATTERN = /(^\/my|\/_layouts\/15\/onedrive\.aspx)$/i
// Office for the web. cloud.microsoft and office.com also host the portal and Outlook, so those
// match only with docId, sourcedoc, resid, or a sharing path. The *.officeapps.live.com editor
// frames are deliberately not matched: Graph cannot resolve a WOPI frame URL to an item.
const CLOUD_MICROSOFT_HOST_PATTERN = /\.cloud\.microsoft$/i
const OFFICE_COM_HOST_PATTERN = /(^|\.)office\.com$/i
const OFFICE_DOCUMENT_PARAMS = ['docId', 'sourcedoc', 'resid'] as const

function isSharePointDocumentUrl(url: URL): boolean {
  return SHAREPOINT_DOCUMENT_PATH_PATTERN.test(url.pathname) && url.searchParams.has('sourcedoc')
}

function isSharePointBrowseItemUrl(url: URL): boolean {
  return SHAREPOINT_BROWSE_PATH_PATTERN.test(url.pathname) && (url.searchParams.get('id') ?? '') !== ''
}

function hasOfficeDocumentLocator(url: URL): boolean {
  const named = OFFICE_DOCUMENT_PARAMS.some((name) => (url.searchParams.get(name) ?? '') !== '')
  return named || SHAREPOINT_SHARING_PATH_PATTERN.test(url.pathname)
}

function isOfficeOnTheWebUrl(url: URL): boolean {
  const host = url.hostname.toLowerCase()
  if (!CLOUD_MICROSOFT_HOST_PATTERN.test(host) && !OFFICE_COM_HOST_PATTERN.test(host)) return false
  return hasOfficeDocumentLocator(url)
}

function isMicrosoftOneDriveUrl(value: string): boolean {
  let url: URL
  try {
    url = new URL(value)
  } catch {
    return false
  }
  if (url.protocol !== 'https:') return false

  const host = url.hostname.toLowerCase()
  if (ONEDRIVE_HOSTS.has(host)) return true
  if (isOfficeOnTheWebUrl(url)) return true
  if (!SHAREPOINT_HOST_PATTERN.test(host)) return false
  return (
    SHAREPOINT_SHARING_PATH_PATTERN.test(url.pathname) || isSharePointDocumentUrl(url) || isSharePointBrowseItemUrl(url)
  )
}

export { isMicrosoftOneDriveUrl }
